import moment from 'moment';
import { getAtsUrl } from '../../../config';
import { invariant } from '../../../utils/commonUtils';
import { decodeJWT } from '../../../utils/encoding/jwt';
import { schema } from '../../../utils/schema';
import { FetchError, statusText } from '../../errors';
import { settings } from '../settings';

/**
 * - 1: Huawei
 * - 2: CVP
 */

const AuthTokenResponseSchema = schema({
  token: 'string',
  expiresIn: 'number',
  vodPlatform: 'number',
});

type AuthTokenResponse = typeof AuthTokenResponseSchema.infer;

interface AuthToken {
  value: string;
  expiresAt: number;
  vodPlatform: 'huawei' | 'cvp';
}

let tokenInteval: NodeJS.Timer | undefined = undefined;

export async function getAuthToken(): Promise<AuthToken> {
  const atsBaseUrl = getAtsUrl();
  const subscriberId = localStorage.getItem('subscriberId');
  invariant(subscriberId !== null, 'Subscriber ID not defined');

  const res = await fetch(`${atsBaseUrl}/auth/token`, settings.auth('POST', JSON.stringify({ subscriberId })));

  if (!res.ok) {
    const text = await statusText(res);
    throw new FetchError(text);
  }

  const data: AuthTokenResponse = await res.json();

  if (AuthTokenResponseSchema.validate(data)) {
    localStorage.setItem('atsToken', data.token);

    localStorage.setItem('vodPlatform', data.vodPlatform === 2 ? 'cvp' : 'huawei');
    const expiresInDate = moment.unix(data.expiresIn);
    const now = moment();

    const interval = expiresInDate.diff(now, 'milliseconds') - 10_000; // Send call 10 sec before expire
    if (tokenInteval) {
      clearInterval(tokenInteval);
    }

    tokenInteval = setInterval(() => {
      getAuthToken();
    }, interval);

    return {
      value: data.token,
      // Expires in is actually the expiration date, not the expiration time 🤷‍♂️
      expiresAt: data.expiresIn,
      vodPlatform: data.vodPlatform === 2 ? 'cvp' : 'huawei',
    };
  }

  throw new Error('Failed to parse auth response.');
}

interface DecodedAuthToken {
  subscriberId: string;
  subscriberCountry: string;
  partnerId: string;
  partnerName: string;
  vodPlatform: '1' | '2';
}

export function decodeAuthToken(token: string): DecodedAuthToken {
  const decoded = decodeJWT(token);

  if (!isDecodedAuthToken(decoded)) {
    throw new Error('Auth token validation failed.');
  }

  return decoded;
}

function isDecodedAuthToken(decoded: unknown): decoded is DecodedAuthToken {
  return (
    typeof decoded === 'object' &&
    decoded !== null &&
    'subscriberId' in decoded &&
    'subscriberCountry' in decoded &&
    'partnerId' in decoded &&
    'partnerName' in decoded &&
    'vodPlatform' in decoded
  );
}
